import { DragEvent, useRef, useState } from "react";
import { useAppData } from "../store";
import type { Deal } from "../types";
import { Btn, Pill, Score } from "../components/Primitives";
import { Ic } from "../components/Icon";
import { ProcessingChip } from "../components/ProcessingChip";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EMAIL_NAMES = /^email\.(txt|md|eml)$/i;

async function collectDroppedItems(items: DataTransferItemList): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  const out: File[] = [];
  await Promise.all(entries.map((e) => walkEntry(e, "", out)));
  return out;
}

function walkEntry(entry: FileSystemEntry, prefix: string, out: File[]): Promise<void> {
  return new Promise((resolve) => {
    if (entry.isFile) {
      (entry as FileSystemFileEntry).file((f) => {
        Object.defineProperty(f, "name", { value: f.name, configurable: true });
        out.push(f);
        resolve();
      }, () => resolve());
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const all: FileSystemEntry[] = [];
      const readBatch = () =>
        reader.readEntries(async (batch) => {
          if (batch.length === 0) {
            await Promise.all(all.map((e) => walkEntry(e, prefix + entry.name + "/", out)));
            resolve();
          } else {
            all.push(...batch);
            readBatch();
          }
        }, () => resolve());
      readBatch();
    } else {
      resolve();
    }
  });
}

interface Props { onOpenDeal: (id: string) => void; }

export function UploadScreen({ onOpenDeal }: Props) {
  const { uploadJob, startUpload, clearUpload } = useAppData();
  const [email, setEmail]   = useState("");
  const [files, setFiles]   = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const phase = uploadJob?.phase ?? "idle";
  const deal  = uploadJob?.deal ?? null;
  const error = localError ?? uploadJob?.error ?? null;

  const hasContent = files.length > 0 || !!email;

  const reset = () => {
    clearUpload();
    setLocalError(null);
    setEmail(""); setFiles([]);
  };

  const applyDropped = async (incoming: File[]) => {
    const emailFile = incoming.find((f) => EMAIL_NAMES.test(f.name));
    if (emailFile) {
      const body = await emailFile.text();
      setEmail(body);
    }
    const atts = incoming.filter((f) => !EMAIL_NAMES.test(f.name));
    if (atts.length) {
      setFiles((current) => {
        const byName = new Map(current.map((f) => [f.name, f] as const));
        atts.forEach((f) => byName.set(f.name, f));
        return [...byName.values()];
      });
    }
  };

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const items = e.dataTransfer.items;
    const dropped = items?.length
      ? await collectDroppedItems(items)
      : Array.from(e.dataTransfer.files);
    if (dropped.length) await applyDropped(dropped);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  const submit = () => {
    if (!email.trim() && files.length === 0) {
      setLocalError("Drop a deal folder first.");
      return;
    }
    setLocalError(null);
    startUpload(email, "", files);
  };

  return (
    <div className="page">
      <div className="topbar">
        <h1>Upload</h1>
        <span className="crumb">· ingest an inbound deal</span>
        <ProcessingChip />
        <span className="grow" />
      </div>

      <div className="scroll" style={{ background: "var(--bg)" }}>
        <div className="up-wrap">
          {phase === "done" && deal
            ? <UploadResult deal={deal} onReset={reset} onOpenDeal={() => onOpenDeal(deal.id)} />
            : (
              <div className="up-card">
                <div className="up-hero">
                  <div className="up-hero-title">Drop a deal in and we'll break it down.</div>
                  <div className="up-hero-sub">
                    Drag in a deal folder and Prism handles the rest — extracts fields, picks the right route, enriches, and scores.
                  </div>
                </div>

                <div
                  className={"up-dropzone" + (dragging ? " dragging" : "")}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragEnter={onDragOver}
                  onDragLeave={() => setDragging(false)}
                >
                  <div className="up-dropzone-icon">
                    <Ic name="folder" size={22} />
                  </div>
                  <div className="up-dropzone-headline">Drop your deal folder here</div>
                  <div className="up-dropzone-sub">
                    or{" "}
                    <a onClick={() => fileRef.current?.click()}>browse files</a>
                  </div>
                  <input
                    ref={fileRef} type="file" multiple
                    accept=".pdf,.txt,.md,.eml,.csv,.doc,.docx,image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const list = Array.from(e.target.files ?? []);
                      if (list.length) applyDropped(list);
                      e.target.value = "";
                    }}
                  />
                </div>

                {hasContent && (
                  <div className="up-detected">
                    {email && (
                      <div className="up-detected-row up-detected-ok">
                        <Ic name="check" size={13} />
                        <span className="up-detected-label">email.txt</span>
                        <span className="up-detected-hint">ready</span>
                      </div>
                    )}
                    {files.map((f) => (
                      <div key={f.name} className="up-detected-row">
                        <Ic name="file" size={13} />
                        <span className="up-detected-label">{f.name}</span>
                        <span className="up-detected-clear"
                              onClick={() => setFiles((cur) => cur.filter((x) => x !== f))}>
                          <Ic name="x" size={11} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {error && <div className="up-error">{error}</div>}

                <div className="up-actions">
                  <Btn kind="primary" onClick={submit} disabled={phase === "working" || !hasContent}>
                    {phase === "working" ? "Processing…" : "Process deal"}
                  </Btn>
                  {hasContent && phase !== "working" && (
                    <Btn kind="ghost" onClick={reset}>Clear</Btn>
                  )}
                  {phase === "working" && (
                    <span className="up-working">
                      <span className="up-spin" />
                      Extracting fields, enriching, scoring…
                    </span>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function UploadResult({
  deal, onReset, onOpenDeal,
}: { deal: Deal; onReset: () => void; onOpenDeal: () => void }) {
  const rejected = deal.status === "rejected";
  return (
    <div className="up-result">
      <div className={"up-result-head" + (rejected ? " rejected" : "")}>
        <div>
          <div className="up-result-co">{deal.co}</div>
          <div className="up-result-sub">{deal.founders} · routed through {deal.route}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Pill kind={deal.status} />
          {rejected
            ? <span className="up-rej-score">Rejected</span>
            : <Score value={deal.score} size="xl" />}
        </div>
      </div>

      {rejected && (
        <div className="up-rej-note">
          Hard filter failed — <b>{deal.rejectedBy}</b>. Filed under Rejected,
          not the main inbox.
        </div>
      )}

      <div className="up-section-label">Extracted fields</div>
      <div className="up-fields">
        {Object.entries(deal.fields).map(([name, cell]) => {
          const value = cell?.value;
          const display = value == null || value === ""
            ? "—"
            : Array.isArray(value) ? value.join(", ")
            : typeof value === "number" ? value.toLocaleString()
            : /^\d{4,}$/.test(String(value)) ? Number(value).toLocaleString()
            : String(value);
          return (
            <div key={name} className="up-field">
              <span className="up-field-name">{name}</span>
              <span className="up-field-val">
                {display === "—" ? <span style={{ color: "var(--ink-4)" }}>—</span> : display}
              </span>
              <span className="up-field-src">{cell?.source ?? "—"}</span>
            </div>
          );
        })}
      </div>

      <div className="up-section-label">Score breakdown</div>
      <div className="up-breakdown">
        {deal.breakdown.map((b, i) => (
          <div key={i} className={"up-bd-row" + (b.passed ? "" : " off")}>
            <span className={"up-bd-dot" + (b.passed ? " on" : "")} />
            <span className="up-bd-label">
              {b.l}
              {b.hard && <span className="up-bd-hard">hard</span>}
            </span>
            <span className="up-bd-mult">
              {b.passed ? "+" + Math.round(b.mult * 10) : 0}
            </span>
          </div>
        ))}
      </div>

      {deal.documents && deal.documents.length > 0 && (
        <>
          <div className="up-section-label">Documents</div>
          <div className="up-docs">
            {deal.documents.map((doc) => {
              const ext = doc.name.split(".").pop()?.toUpperCase() || "FILE";
              return (
                <a
                  key={doc.name}
                  className="up-doc-card"
                  href={`/api/deals/${deal.id}/documents/${encodeURIComponent(doc.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="up-doc-ext">{ext}</span>
                  <span className="up-doc-info">
                    <span className="up-doc-name">{doc.name}</span>
                    <span className="up-doc-size">{formatFileSize(doc.size)}</span>
                  </span>
                  <Ic name="ext" size={13} />
                </a>
              );
            })}
          </div>
        </>
      )}

      <div className="up-result-actions">
        <button className="up-btn-view" onClick={onOpenDeal}>
          <Ic name="ext" size={13} />
          View deal
        </button>
        <button className="up-btn-another" onClick={onReset}>
          <Ic name="plus" size={13} />
          Process another
        </button>
      </div>
    </div>
  );
}
